import { VideoOff } from "lucide-react"
import { useTranslation } from "react-i18next"

import { cn } from "@/lib/utils"

/**
 * OKS-840 — "no brief video" placeholder for the §08 creator mission detail.
 *
 * Occupies the brief-video slot when the mission has NO brief video attached
 * (`brief_video.attached === false`). Before this, that arm of the gate
 * rendered nothing at all, so a missing video was indistinguishable from a
 * page that failed to load — a prod tester read it as breakage (PRD §0).
 *
 * DELIBERATELY NOT a branch inside `BriefVideo`: that component owns the
 * "Watch the brief" `<h2>`, and a heading above "there is no video"
 * contradicts itself (UX Spec §2). Keeping the empty case in its own file is
 * also what lets the error+Retry and player paths stay byte-unchanged.
 *
 * This is the FINAL answer for the mission, not a transient — no skeleton, no
 * animation, no `role="alert"`/`role="status"` (it is present on load, not an
 * announcement), and nothing focusable or tappable (UX Spec §3, §4, §6). The
 * `VideoOff` glyph is decorative; the sentence alone carries the meaning.
 *
 * NOTE (OKS-697): do NOT add `aspect-video` here to mirror the player's
 * footprint. This file is not in `COVER_SURFACES`, so the mission-cover
 * ratio-parity guard would never see it, and a 16:9 box would silently
 * reintroduce the crop that guard exists to prevent.
 */
export interface BriefVideoEmptyProps {
  className?: string
}

export function BriefVideoEmpty({ className }: BriefVideoEmptyProps) {
  const { t } = useTranslation()

  return (
    <div
      data-testid="mission-detail-brief-video-empty"
      className={cn(
        "border-input text-muted-foreground rounded-md border border-dashed p-6 text-center text-sm",
        className,
      )}
    >
      <p className="flex items-center justify-center gap-2">
        <VideoOff className="size-4 shrink-0" aria-hidden="true" />
        {t("creator_mission_detail.brief_video.empty")}
      </p>
    </div>
  )
}
